import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { CREATE_TOPIC_MUTATION } from '../../graphql';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DOMPurify from 'dompurify';
import { decode } from 'he';
import { apiFetch } from '../../lib/apiFetch';

function draftStorageKey(accountId: string) {
  return `smagent:library-create-draft:${accountId}`;
}

interface CreateTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  onSuccess?: () => void;
  /** Called with the new topic id after create (e.g. navigate to My Content detail). */
  onCreated?: (id: string) => void;
}

// The older topic create (the Reach post form's picker). Content → Articles'
// New article is New article ▾ → the article editor (2026-10-25).
type ContentType = 'webpage' | 'video' | 'image';
type MediaType = 'PHOTO' | 'VIDEO' | 'BOOKMARK';

interface TopicFormData {
  contentType: ContentType;
  title: string;
  destinationUrl: string;
  summary: string;
  bodyText: string;
  mediaUrl: string;
  keywords: string;
  category: string;
}

interface MetadataResponse {
  success: boolean;
  data?: {
    title?: string;
    summary?: string;
    image?: string;
  };
  error?: string;
  message?: string;
}

interface UploadResponse {
  url?: string;
  error?: string;
}

const inputClass =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary';

const emptyForm: TopicFormData = {
  contentType: 'webpage',
  title: '',
  destinationUrl: '',
  summary: '',
  bodyText: '',
  mediaUrl: '',
  keywords: '',
  category: '',
};

type FieldErrors = { title?: string; mediaUrl?: string };

function contentTypeToMediaType(contentType: ContentType): MediaType {
  if (contentType === 'video') return 'VIDEO';
  if (contentType === 'image') return 'PHOTO';
  return 'BOOKMARK';
}

function toSafeHtml(raw?: string | null): string {
  if (!raw) return '';
  let decoded = raw;
  for (let i = 0; i < 3; i++) {
    const next = decode(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  return DOMPurify.sanitize(decoded, { USE_PROFILES: { html: true } });
}

export const CreateTopicModal: React.FC<CreateTopicModalProps> = ({
  isOpen,
  onClose,
  accountId,
  onSuccess,
  onCreated,
}) => {
  const blankForm = emptyForm;
  const types: ContentType[] = ['webpage', 'video', 'image'];
  const isText = (t: ContentType) => t === 'webpage';
  const [formData, setFormData] = useState<TopicFormData>(blankForm);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shown on the field itself, and cleared as soon as it is filled in.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const titleRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const skipDraftLoadRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = React.useMemo(
    () => `file-input-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  // Whichever page, filter and search they are showing: the Reach topic
  // picker and the Content list.
  const refetchQueries = ['GetTopicPicker', 'GetTopicsPage'];
  const [createTopic, { loading: createLoading }] = useMutation(CREATE_TOPIC_MUTATION, {
    refetchQueries,
  });

  const handleClose = useCallback(() => {
    // Keep local draft so writers can leave and resume.
    setError(null);
    setFieldErrors({});
    onClose();
  }, [onClose]);

  // Restore local draft when opening.
  useEffect(() => {
    if (!isOpen) return;
    skipDraftLoadRef.current = true;
    try {
      const raw = localStorage.getItem(draftStorageKey(accountId));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<TopicFormData>;
        const next = { ...blankForm, ...parsed };
        // A draft started in the other create keeps its text type.
        if (!types.includes(next.contentType)) next.contentType = blankForm.contentType;
        setFormData(next);
        setDraftSavedAt(new Date());
      } else {
        setFormData(blankForm);
        setDraftSavedAt(null);
      }
    } catch {
      setFormData(blankForm);
      setDraftSavedAt(null);
    }
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, accountId]);

  // Autosave draft to localStorage while composing.
  useEffect(() => {
    if (!isOpen) return;
    if (skipDraftLoadRef.current) {
      skipDraftLoadRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(draftStorageKey(accountId), JSON.stringify(formData));
        setDraftSavedAt(new Date());
      } catch {
        // ignore quota / private mode
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData, isOpen, accountId]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) return;
      // Let the fullscreen body editor consume Escape first.
      if (document.querySelector('[data-rich-text-expanded="true"]')) return;
      handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  const discardDraft = () => {
    localStorage.removeItem(draftStorageKey(accountId));
    setFormData(blankForm);
    setDraftSavedAt(null);
    setError(null);
    setFieldErrors({});
  };

  const setField = <K extends keyof TopicFormData>(key: K, value: TopicFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    // An error about this field is answered by changing it.
    if (key === 'title' || key === 'mediaUrl' || key === 'contentType') {
      setFieldErrors((prev) =>
        key === 'contentType' ? { ...prev, mediaUrl: undefined } : { ...prev, [key]: undefined }
      );
    }
  };

  const handleUrlFetch = async () => {
    const url = formData.destinationUrl.trim();
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    try {
      const urlObj = new URL(url);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        setError('Invalid URL');
        return;
      }

      setMetadataLoading(true);
      setError(null);

      const response = await apiFetch(
        `/api/utilities/metadata?url=${encodeURIComponent(url)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        const errorData: MetadataResponse = await response
          .json()
          .catch(() => ({ success: false, error: 'Failed to fetch metadata' }));
        throw new Error(
          errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result: MetadataResponse = await response.json();
      if (result.success && result.data) {
        setFormData((prev) => ({
          ...prev,
          title: result.data?.title || prev.title || '',
          summary: result.data?.summary || prev.summary || '',
          mediaUrl: result.data?.image || prev.mediaUrl || '',
          destinationUrl: url,
          contentType: prev.contentType,
        }));
      } else {
        setError('Failed to fetch metadata from URL');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  const processFile = async (file: File) => {
    if (!file) return;

    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = [
      'video/mp4',
      'video/mpeg',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
    ];

    const isImage = allowedImageTypes.includes(file.type);
    const isVideo = allowedVideoTypes.includes(file.type);

    if (!isImage && !isVideo) {
      setError('Please upload an image or video file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formDataObj = new FormData();
      // Stored under this site's folder (and only if you're on it).
      if (accountId) formDataObj.append('accountId', accountId);
      formDataObj.append('file', file);
      const response = await apiFetch('/api/upload/media', {
        method: 'POST',
        body: formDataObj,
      });

      if (!response.ok) {
        const errorData: UploadResponse = await response
          .json()
          .catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || `Upload failed with status ${response.status}`);
      }

      const result: UploadResponse = await response.json();
      if (!result.url) throw new Error('No URL returned from server');

      setFormData((prev) => ({
        ...prev,
        mediaUrl: result.url!,
        contentType: isVideo ? 'video' : 'image',
      }));
      setFieldErrors((prev) => ({ ...prev, mediaUrl: undefined }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const contentType = formData.contentType;
    const mediaType = contentTypeToMediaType(contentType);
    const mediaUrl = formData.mediaUrl.trim();

    const problems: FieldErrors = {
      ...(!formData.title.trim() ? { title: 'Add a title.' } : {}),
      ...((contentType === 'video' || contentType === 'image') && !mediaUrl
        ? { mediaUrl: `A${contentType === 'image' ? 'n image' : ' video'} needs its file or link.` }
        : {}),
    };
    if (problems.title || problems.mediaUrl) {
      setFieldErrors(problems);
      (problems.title ? titleRef : mediaRef).current?.focus();
      return;
    }
    setFieldErrors({});

    const summaryHtml = formData.summary === '<p></p>' ? '' : toSafeHtml(formData.summary);
    const bodyHtml = isText(contentType)
      ? formData.bodyText === '<p></p>'
        ? ''
        : toSafeHtml(formData.bodyText)
      : undefined;

    try {
      const result = await createTopic({
        variables: {
          input: {
            accountId,
            title: formData.title.trim(),
            summary: summaryHtml || undefined,
            bodyText: bodyHtml || undefined,
            mediaType,
            mediaUrl: mediaUrl || '',
            destinationUrl: formData.destinationUrl.trim() || undefined,
            keywords: formData.keywords.trim() || undefined,
            category: formData.category.trim() || undefined,
            contentType,
          },
        },
      });

      const newId = result.data?.createTopic?.id as string | undefined;
      localStorage.removeItem(draftStorageKey(accountId));
      setFormData(blankForm);
      setDraftSavedAt(null);
      setError(null);
      onClose();
      if (newId && onCreated) onCreated(newId);
      else if (onSuccess) onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create content');
    }
  };

  const draftStatus = draftSavedAt
    ? `Draft saved locally ${format(draftSavedAt, 'p')}`
    : 'Draft autosave on';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="flex justify-between items-start gap-3">
            <div>
              <h3 className="text-xl font-semibold text-cmp-ink">New Content</h3>
              <p className="text-xs text-cmp-ink-3 mt-1">{draftStatus}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={discardDraft}
                className="text-xs text-cmp-ink-2 hover:text-cmp-ink underline"
              >
                Discard draft
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="text-cmp-ink-2 hover:text-cmp-ink transition-colors"
                title="Close (Esc) — draft is kept"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Content type
            </label>
            {/* The app's segmented control (ink-seg), like every other either/or choice. */}
            <div className="ink-seg" role="radiogroup" aria-label="Type">
              {types.map((type) => (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={formData.contentType === type}
                  data-active={formData.contentType === type ? 'true' : undefined}
                  onClick={() => setField('contentType', type)}
                  className="capitalize"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor={`${fileInputId}-title`}
              className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5"
            >
              Title <span className="text-error">*</span>
            </label>
            <Input
              id={`${fileInputId}-title`}
              ref={titleRef}
              value={formData.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="Title"
              aria-invalid={fieldErrors.title ? true : undefined}
              aria-describedby={fieldErrors.title ? `${fileInputId}-title-error` : undefined}
              className={`${inputClass} text-lg font-semibold ${
                fieldErrors.title ? 'border-error focus:ring-error' : ''
              }`}
            />
            {fieldErrors.title ? (
              <p id={`${fileInputId}-title-error`} className="text-xs text-error mt-1">
                {fieldErrors.title}
              </p>
            ) : null}
          </div>

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Public link
            </label>
            <div className="flex gap-2">
              <Input
                type="url"
                value={formData.destinationUrl}
                onChange={(e) => setField('destinationUrl', e.target.value)}
                placeholder="https://example.com/page"
                className={inputClass}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleUrlFetch}
                disabled={metadataLoading || !formData.destinationUrl.trim()}
              >
                {metadataLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
              </Button>
            </div>
            <p className="text-xs text-cmp-ink-3 mt-1">
              Optionally fetch title, summary, and thumbnail from the URL.
            </p>
          </div>

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Media {!isText(formData.contentType) && <span className="text-error">*</span>}
            </label>
            <Input
              ref={mediaRef}
              type="url"
              value={formData.mediaUrl}
              onChange={(e) => setField('mediaUrl', e.target.value)}
              placeholder="https://example.com/photo.jpg"
              aria-invalid={fieldErrors.mediaUrl ? true : undefined}
              className={`${inputClass} ${fieldErrors.mediaUrl ? 'border-error focus:ring-error' : ''}`}
            />
            {fieldErrors.mediaUrl ? (
              <p className="text-xs text-error mt-1">{fieldErrors.mediaUrl}</p>
            ) : null}
            <label
              htmlFor={fileInputId}
              className={`mt-2 block relative ${uploading ? 'cursor-wait' : 'cursor-pointer'}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (uploading) return;
                const file = e.dataTransfer.files[0];
                if (file) await processFile(file);
              }}
            >
              <div className="w-full px-3 py-6 border border-dashed border-cmp-line-2 rounded text-cmp-ink hover:border-primary transition-colors flex items-center justify-center gap-2 bg-form-dark/40 text-sm">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <ImagePlus className="w-4 h-4" />
                    Upload image or video, or drag and drop
                  </>
                )}
              </div>
              <input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await processFile(file);
                }}
                disabled={uploading}
              />
            </label>
            {formData.mediaUrl && (
              <div className="mt-2 relative bg-form-dark rounded border border-cmp-line-2 overflow-hidden">
                {formData.contentType === 'video' ? (
                  <video
                    src={formData.mediaUrl}
                    controls
                    className="w-full max-h-48 object-contain"
                  />
                ) : (
                  <img
                    src={formData.mediaUrl}
                    alt="Preview"
                    className="w-full max-h-48 object-contain"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setField('mediaUrl', '')}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                >
                  <X className="w-4 h-4 text-cmp-ink" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Summary
            </label>
            <RichTextEditor
              value={formData.summary}
              onChange={(html) => setField('summary', html)}
              placeholder="Summary"
            />
          </div>

          {isText(formData.contentType) && (
            <div>
              <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
                Body
              </label>
              <RichTextEditor
                value={formData.bodyText}
                onChange={(html) => setField('bodyText', html)}
                placeholder="Full article body — expand for a distraction-free writing surface"
                expandable
                expandLabel={formData.title.trim() || 'Body'}
                status={draftStatus}
              />
            </div>
          )}

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Category
            </label>
            <Input
              value={formData.category}
              onChange={(e) => setField('category', e.target.value)}
              placeholder="Category"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
              Keywords
            </label>
            <Input
              value={formData.keywords}
              onChange={(e) => setField('keywords', e.target.value)}
              placeholder="Keywords (comma-separated)"
              className={inputClass}
            />
          </div>

          <p className="text-xs text-cmp-ink-3">
            Work-in-progress is autosaved in this browser. After create, publish or schedule from
            the detail page.
          </p>

          {error && (
            <div className="p-3 bg-error/20 border border-error rounded text-error text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={createLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={createLoading}>
              {createLoading ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
